// Phase 68 VIS-10 — StudioGalleryLightbox (HTMLDialogElement-native)
//
// Click on a `StudioGallery` tile opens this modal lightbox plein-écran.
// Close paths : ESC key, click on backdrop, vertical touch swipe (>80px),
// explicit "Fermer" button. Focus is restored to the element that was active
// before `showModal()` was called.
//
// Design decision (Gate 1 validated 2026-06-22) : vanilla React 19 +
// HTMLDialogElement.showModal() native, ZERO deps. `<dialog>` provides :
//   - top-layer rendering (no portal needed)
//   - native focus trap inside the dialog (Tab cycle bounded)
//   - native ::backdrop pseudo-element for the overlay
//   - free ESC key handling via the cancel/close events
// We supplement with :
//   - useRef<HTMLDialogElement> + useEffect to toggle .showModal()/.close()
//     based on the `isOpen` prop
//   - onClick handler comparing e.target === e.currentTarget to detect
//     backdrop clicks (the click event target IS the dialog itself when the
//     user clicks the ::backdrop area)
//   - onTouchStart/onTouchEnd to capture vertical swipe gestures (mobile)
//   - useRef snapshot of document.activeElement BEFORE showModal(), restored
//     in cleanup (jsdom doesn't restore automatically, and even Chrome's
//     behavior here is "best effort" — we own this contract explicitly)
//
// motion-reduce : the dialog className uses `motion-safe:animate-fadeIn`
// (Tailwind 4 motion-safe variant + custom keyframe). On `prefers-reduced-
// motion: reduce`, the animation is dropped — the dialog appears with opacity
// 1, no transition. We deliberately do NOT use translate/scale animations
// (would feel jumpy on reduced-motion users + ignore brand minimalism).
//
// Refs: phase 68, VIS-10

import { useEffect, useRef } from 'react'
import { studioPictures, type StudioId } from '../../assets/studio'
import { useLanguage } from '../../i18n/LanguageProvider'

export interface StudioGalleryLightboxProps {
  /** Whether the lightbox is currently open. Toggling true triggers showModal(). */
  isOpen: boolean
  /** Caller-owned close handler. Fired on ESC, backdrop click, swipe, or Fermer. */
  onClose: () => void
  /** Which studio photo to render zoomed. null = render nothing. */
  photoId: StudioId | null
  /** Accessible label — mirrored on both the <img alt> and the <dialog aria-label>. */
  alt: string
}

// Swipe gesture threshold in CSS pixels (vertical). Below this delta, the
// touchend is treated as a tap/pan and ignored. 80px is a common library
// default (yet-another-react-lightbox, react-image-lightbox).
const SWIPE_THRESHOLD_PX = 80

export default function StudioGalleryLightbox({
  isOpen,
  onClose,
  photoId,
  alt,
}: StudioGalleryLightboxProps) {
  const { t } = useLanguage()
  const dialogRef = useRef<HTMLDialogElement>(null)
  const previouslyFocusedRef = useRef<HTMLElement | null>(null)
  const touchStartYRef = useRef<number | null>(null)

  // Sync the imperative <dialog> open state with the `isOpen` prop.
  // On open : snapshot the currently focused element, then call showModal().
  // On close : call .close() if still open (idempotent — checking .open guards
  // against double-close when the close event fires from inside the dialog).
  useEffect(() => {
    const dlg = dialogRef.current
    if (!dlg) return

    if (isOpen && !dlg.open) {
      previouslyFocusedRef.current =
        (document.activeElement as HTMLElement | null) ?? null
      dlg.showModal()
    } else if (!isOpen && dlg.open) {
      dlg.close()
    }
  }, [isOpen])

  // Listen to the dialog's native `close` event (fires on programmatic
  // .close()), and EXPLICITLY listen to ESC key on document while open.
  // We can't rely on the native `cancel→close` event alone because :
  //   (a) jsdom doesn't fire it (tests need an explicit handler)
  //   (b) real browsers DO fire it, but listening to a document-level keydown
  //       is a more robust contract (works the same in test + prod, doesn't
  //       depend on which element has focus inside the dialog)
  // We propagate both paths to the caller AND restore focus to the previously
  // focused element. The close-handler is wired to run exactly once per close
  // cycle via an `isClosingRef` guard — important because the document keydown
  // ALSO triggers the native cancel→close in real browsers, which would
  // otherwise fire onClose() twice.
  useEffect(() => {
    if (!isOpen) return
    const dlg = dialogRef.current
    if (!dlg) return

    let closed = false
    const close = () => {
      if (closed) return
      closed = true
      const prev = previouslyFocusedRef.current
      if (prev && typeof prev.focus === 'function' && document.contains(prev)) {
        prev.focus()
      }
      onClose()
    }
    const handleNativeClose = () => close()
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault()
        close()
      }
    }
    dlg.addEventListener('close', handleNativeClose)
    document.addEventListener('keydown', handleKeyDown)
    return () => {
      dlg.removeEventListener('close', handleNativeClose)
      document.removeEventListener('keydown', handleKeyDown)
    }
  }, [isOpen, onClose])

  // Click handler on the <dialog> element itself. When the user clicks the
  // ::backdrop (the dimmed area outside the picture), the event target IS the
  // dialog element. When they click the picture/button/img inside, the target
  // is the inner node — bubbling reaches us but target !== currentTarget.
  const handleBackdropClick = (e: React.MouseEvent<HTMLDialogElement>) => {
    if (e.target === e.currentTarget) {
      onClose()
    }
  }

  // Touch swipe (vertical >80px) → close. Stored on a ref so we don't
  // re-render on every move and keep the handler trivial.
  const handleTouchStart = (e: React.TouchEvent<HTMLDialogElement>) => {
    const t = e.touches[0]
    touchStartYRef.current = t ? t.clientY : null
  }
  const handleTouchEnd = (e: React.TouchEvent<HTMLDialogElement>) => {
    const start = touchStartYRef.current
    touchStartYRef.current = null
    if (start === null) return
    const t = e.changedTouches[0]
    if (!t) return
    if (Math.abs(t.clientY - start) > SWIPE_THRESHOLD_PX) {
      onClose()
    }
  }

  // Defensive: nothing to render without a photoId. We still render the
  // <dialog> shell (so refs are stable across open/close cycles), but with
  // empty children — the L1/L10 tests cover this.
  const pic = photoId ? studioPictures[photoId] : null

  return (
    <dialog
      ref={dialogRef}
      aria-label={alt || undefined}
      onClick={handleBackdropClick}
      onTouchStart={handleTouchStart}
      onTouchEnd={handleTouchEnd}
      // Tailwind 4 :
      //   - bg-transparent + the native ::backdrop CSS (in index.css) gives
      //     the dimmed overlay
      //   - max-h-screen / max-w-screen / w-full / h-full = fullscreen container
      //   - p-0 / border-0 = strip the user-agent default chrome
      //   - text-text + bg-bg keep the dialog readable when content overflows
      //   - motion-safe:animate-fadeIn = simple opacity 0→1 (200ms). On
      //     prefers-reduced-motion: reduce, the animation is dropped.
      className="m-0 p-0 border-0 w-full h-full max-w-screen max-h-screen bg-bg/95 backdrop-blur-sm text-text motion-safe:animate-[fadeIn_0.2s_ease-out]"
    >
      {pic ? (
        <div className="relative w-full h-full flex items-center justify-center">
          <picture>
            <source type="image/avif" srcSet={pic.sources.avif} />
            <source type="image/webp" srcSet={pic.sources.webp} />
            <img
              src={pic.img.src}
              width={pic.img.w}
              height={pic.img.h}
              alt={alt}
              decoding="async"
              className="object-contain max-h-screen w-auto mx-auto"
            />
          </picture>
          <button
            type="button"
            onClick={onClose}
            aria-label={t('studio.lightbox.close')}
            // Top-right close button. Tailwind: accessible target (≥44px), high
            // contrast against any photo background, motion-safe hover.
            className="absolute top-4 right-4 w-11 h-11 flex items-center justify-center rounded-full bg-bg/80 text-text border border-border motion-safe:transition-colors hover:bg-bg hover:text-accent focus:outline-none focus:ring-2 focus:ring-accent"
          >
            {/* X icon, inline SVG to avoid icon-lib dependency */}
            <svg
              aria-hidden="true"
              width="20"
              height="20"
              viewBox="0 0 20 20"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <line x1="5" y1="5" x2="15" y2="15" />
              <line x1="15" y1="5" x2="5" y2="15" />
            </svg>
          </button>
        </div>
      ) : null}
    </dialog>
  )
}
